import api from './apiClient';

// Create a new Stripe payment intent
export const createPaymentIntent = async (amount, currency = 'usd') => {
  try {
    const response = await api.post('/payments/create-intent/', { amount, currency });
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to create payment intent' };
  }
};

// Create a Stripe setup intent for saving card details
export const createSetupIntent = async () => {
  try {
    const response = await api.post('/payments/create-setup-intent');
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to create setup intent' };
  }
};

// Create a new subscription
export const createSubscription = async (subscriptionData) => {
  try {
    const response = await api.post('/subscriptions/', subscriptionData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to create subscription' };
  }
};

// Create subscription with Stripe
export const createStripeSubscription = async (packageId, paymentMethodId, trialPeriodDays = 0) => {
  try {
    const response = await api.post('/subscriptions/stripe', {
      package_id: packageId,
      payment_method_id: paymentMethodId,
      trial_period_days: trialPeriodDays > 0 ? trialPeriodDays : 0
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to create subscription' };
  }
};

// Get user's active subscriptions
export const getUserSubscriptions = async () => {
  try {
    const response = await api.get('/subscriptions/user');
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to fetch subscriptions' };
  }
};

// Get details for a specific subscription
export const getSubscriptionDetails = async (subscriptionId) => {
  try {
    const response = await api.get(`/subscriptions/${subscriptionId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to fetch subscription details' };
  }
};

// Cancel a subscription
export const cancelSubscription = async (subscriptionId, reason) => {
  try {
    const response = await api.post(`/subscriptions/${subscriptionId}/cancel`, { reason });
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to cancel subscription' };
  }
};

// Reactivate a subscription that was pending cancellation
export const reactivateSubscription = async (subscriptionId) => {
  try {
    const response = await api.post(`/subscriptions/${subscriptionId}/reactivate`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to reactivate subscription' };
  }
};

// Update payment method for a subscription
export const updatePaymentMethod = async (subscriptionId, paymentMethodId) => {
  try {
    const response = await api.put(`/subscriptions/${subscriptionId}/payment-method/`, { 
      payment_method_id: paymentMethodId 
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to update payment method' };
  }
};

// Get user's saved payment methods
export const getUserPaymentMethods = async () => {
  try {
    const response = await api.get('/payments/methods/');
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to fetch payment methods' };
  }
};

// Delete a saved payment method
export const deletePaymentMethod = async (paymentMethodId) => {
  try {
    const response = await api.delete(`/payments/methods/${paymentMethodId}/`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to delete payment method' };
  }
};

// Set a payment method as default
export const setDefaultPaymentMethod = async (paymentMethodId) => {
  try {
    const response = await api.post(`/payments/methods/${paymentMethodId}/default/`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to set default payment method' };
  }
};

// Get invoice history for a subscription
export const getSubscriptionInvoices = async (subscriptionId) => {
  try {
    const response = await api.get(`/subscriptions/${subscriptionId}/invoices/`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to fetch invoice history' };
  }
};

// Get all invoices for a user
export const getUserInvoices = async () => {
  try {
    const response = await api.get('/payments/invoices/');
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to fetch invoices' };
  }
};

// Process one-time payment for company services
export const createServicePayment = async (serviceId, paymentMethodId) => {
  try {
    const response = await api.post('/payments/service-payment', {
      service_id: serviceId,
      payment_method_id: paymentMethodId
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to process payment' };
  }
};

// Get package by id
export const getPackageById = async (packageId) => {
  try {
    const response = await api.get(`/subscriptions/packages/${packageId}/`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to fetch package details' };
  }
};

// Get user's purchase history
export const getPurchaseHistory = async () => {
  try {
    const response = await api.get('/profiles/purchase-history');
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to fetch purchase history' };
  }
};

export default {
  createPaymentIntent,
  createSetupIntent,
  createSubscription,
  createStripeSubscription,
  getUserSubscriptions,
  getSubscriptionDetails,
  cancelSubscription,
  reactivateSubscription,
  updatePaymentMethod,
  getUserPaymentMethods,
  deletePaymentMethod,
  setDefaultPaymentMethod,
  getSubscriptionInvoices,
  getUserInvoices,
  createServicePayment,
  getPackageById,
  getPurchaseHistory
}; 